import {
    RiAddLine, RiArrowDownSLine, RiEdit2Line,
    RiQuestionLine,
} from "@remixicon/react";
import React, {
    ReactElement,
    useState,
} from "react";
import { RiDeleteBinLine } from "react-icons/ri";
import {
    CloseSettingsDocument, GetSettingsDocument, GetSeverityLabelsDocument, SettingsSlaTierInput,
    SettingsSlaTierMetricValue,
    SettingsSlaTierValue, SlaMetric, TagsSettingsDocument, useSaveSettingsMutation,
} from "../../../../__generated__/api";
import { cloneWithoutTypename } from "../../../../api";
import { useFormBuilder } from "../../../../components/dirty-tracker/FormTracker";
import EditableControl, { ControlType } from "../../../../components/editable-text/EditableText";
import DurationPicker from "../../../../components/pickers/time/DurationPicker";
import Popover from "../../../../components/popover/Popover";
import Select from "../../../../components/select/Select";
import TagList from "../../../../components/tag-list/TagList";
import Alert from "../../../../components/theme/alert/Alert";
import Button from "../../../../components/theme/button/Button";
import Dropdown from "../../../../components/theme/dropdown/Dropdown";
import SettingsContainer from "../../../../components/theme/settings";
import Text from "../../../../components/theme/text/Text";
import Toggle from "../../../../components/theme/toggle/Toggle";
import useArticleContext from "../../../../contexts/HelpContext";
import { LoggingHandler } from "../../../../contexts/logging/LoggingContext";
import { Time, timeToHours } from "../../../../date";
import { DEEP_COMPARER, SettingsProps } from "../base";
import Styles from "./SLA.module.scss";

const POLICIES = [
    {
        metric: SlaMetric.TimeToFirstResponse,
        title: "Time to first response",
        description: (
            <div>
                This is the amount of time it takes for a
                support agent to respond to a customer&apos;s initial inquiry.
                Foqal Agent&apos;s time to
                first response policy helps you ensure that a support agent responds to the
                customer&apos;s request within a specific time frame.
            </div>
        ),
    },
    {
        metric: SlaMetric.TimeToUpdate,
        title: "Time to update",
        description: (
            <div>
                This policy ensures that an agent updates the customer periodically,
                usually within a specific time frame. Time to
                update policy helps to keep customers informed
                and up-to-date on the status of their request. For example, in the case
                of a major emergency, a customer might ask to be updated every 4 hours
                until the issue is resolved. This policy helps you keep track of how much
                time elapsed since the last customer update.
            </div>
        ),
    },
    {
        metric: SlaMetric.RequesterWaitTime,
        title: "Requester Wait Time",
        description: (
            <div>
                This policy measures the amount of time it takes to update the requester
                after the requester adds a subsequent message after the request.
                Foqal Agent&apos;s requester wait time policy helps ensure that the requester
                receives a response within a specific time frame, after they add
                additional context or an additional question.
            </div>
        ),
    },
];

function timeToMilliseconds(value: string): number {
    return timeToHours(value) * Time.MINUTE;
}
function PresetTiersComponent({
    setSlaValues,
    severityLabels,
    setDefaultSLAPolicyIndex,
}: {
    setSlaValues: (value: SettingsSlaTierInput[]) => void,
    severityLabels: string[];
    setDefaultSLAPolicyIndex: (value: number) => void
}): ReactElement {
    return (
        <Dropdown>
            <Dropdown.Toggle
                variant="outline-blue"
                size="small"
                postfixIcon={<RiArrowDownSLine />}
            >
                Create from Examples
            </Dropdown.Toggle>
            <Dropdown.Menu>
                <Dropdown.Item onClick={() => {
                    setSlaValues([
                        {
                            name: "Enterprise",
                            description: "Top tier for the best customers",
                            values: severityLabels.map((__, severityIndex) => ({
                                policies: [
                                    {
                                        metric: SlaMetric.TimeToFirstResponse,
                                        shouldAccountHoursOfOperation: false,
                                        value: severityIndex === 0
                                            ? 30 * Time.MINUTE
                                            : timeToMilliseconds(`${2 ** (severityIndex - 1)}:00`),
                                    },
                                    {
                                        metric: SlaMetric.RequesterWaitTime,
                                        shouldAccountHoursOfOperation: true,
                                        value: timeToMilliseconds(`04:00`),
                                    },
                                ],
                            })),
                        },
                        {
                            name: "Premium",
                            description: "Second highest for the 2nd best customers",
                            values: severityLabels.map((__, severityIndex) => ({
                                policies: [
                                    {
                                        metric: SlaMetric.TimeToFirstResponse,
                                        shouldAccountHoursOfOperation: false,
                                        value: timeToMilliseconds(`${2 ** (severityIndex + 0)}:00`),
                                    },
                                    {
                                        metric: SlaMetric.RequesterWaitTime,
                                        shouldAccountHoursOfOperation: true,
                                        value: timeToMilliseconds(`08:00`),
                                    },
                                ],

                            })),
                        },
                        {
                            name: "Free",
                            description: "Free tier for free customers. ",
                            values: severityLabels.map(() => ({
                                policies: [
                                    {
                                        metric: SlaMetric.TimeToFirstResponse,
                                        shouldAccountHoursOfOperation: false,
                                        value: timeToMilliseconds("00:00"),
                                    },
                                    {
                                        metric: SlaMetric.RequesterWaitTime,
                                        shouldAccountHoursOfOperation: false,
                                        value: timeToMilliseconds("00:00"),
                                    },
                                ],
                            })),
                        },
                    ]);
                    setDefaultSLAPolicyIndex(1);
                }}
                >
                    Customer Support - 3 Tiers
                </Dropdown.Item>
                <Dropdown.Item onClick={() => {
                    setSlaValues([
                        {
                            name: "Internal",
                            description: "SLA policy for internal support",
                            values: severityLabels.map((__, severityIndex) => ({
                                policies: [
                                    {
                                        metric: SlaMetric.TimeToFirstResponse,
                                        shouldAccountHoursOfOperation: true,
                                        value: severityIndex === 0
                                            ? 30
                                            : timeToMilliseconds(`${2 ** (severityIndex - 1)}:00`),
                                    },
                                    {
                                        metric: SlaMetric.TimeToUpdate,
                                        shouldAccountHoursOfOperation: true,
                                        value: timeToMilliseconds(`04:00`),
                                    },
                                ],

                            })),
                        },
                    ]);
                    setDefaultSLAPolicyIndex(0);
                }}
                >
                    Employee Support
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    );
}

interface SLATierRowProps {
    label: string;
    value: SettingsSlaTierValue;
    onChange: (value: SettingsSlaTierValue) => void
}

function SLATierValueRow(props: SLATierRowProps): ReactElement {
    const policyMap = props.value?.policies?.toIdMap("metric") ?? {};
    const policies = POLICIES.map(metric => policyMap[metric.metric] ?? { metric: metric.metric } as SettingsSlaTierMetricValue);

    const onChange = (value: SettingsSlaTierMetricValue) => {
        const copy = [...props.value?.policies ?? []];
        const current = copy.findIndex(row => row.metric === value.metric);
        if (current < 0) {
            copy.push({
                ...value,
            });
        } else {
            copy[current] = {
                ...copy[current],
                ...value,
            };
        }

        props.onChange({
            policies: copy,
        });
    };

    return (
        <div className={Styles["policy-row"]}>
            <Text textVariant="body-2-medium">{props.label}</Text>
            {policies.map(policy => (
                <div className={Styles["policy-item"]}>
                    <DurationPicker
                        value={policy.value as number}
                        className={Styles["policy-time"]}
                        placeholder=""
                        onChange={time => {
                            onChange({
                                ...policy,
                                value: time?.time,
                            });
                        }}
                    />
                    <Toggle
                        text="Work Hours"
                        checked={policy.shouldAccountHoursOfOperation ?? false}
                        onChange={event => {
                            onChange({
                                ...policy,
                                shouldAccountHoursOfOperation: event.target.checked,
                            });
                        }}
                    />
                </div>
            ))}

        </div>
    );
}
interface SLATierProps {
    name: string;
    description: string | undefined;
    severityLabels: string[];
    severityValues: SettingsSlaTierValue[];
    onChange: (value: ({ name?: string; description?: string; values?: SettingsSlaTierValue[] })) => void
    onDelete: () => void
}

function SLATierRow(props: SLATierProps): ReactElement {
    const { severityValues } = props;
    return (
        <div className={Styles.policy}>
            <div className={Styles["policy-header"]}>
                <div>
                    <Text as="h3" textVariant="subtitle-2">
                        <EditableControl
                            isFormField
                            editIcon={<RiEdit2Line className={Styles["editable-control-icon"]} />}
                            placeholder="SLA Policy name"
                            inputClassName={Styles["editable-control"]}
                            value={props.name}
                            isEditing={props.name === ""}
                            onChange={value => props.onChange({
                                name: value as string,
                                description: props.description,
                                values: severityValues,
                            })}
                        />
                    </Text>
                    <Text textVariant="body-2-regular" colorVariant="body-secondary">
                        <EditableControl
                            isFormField
                            type={ControlType.LONG_TEXT}
                            inputClassName={Styles["editable-control"]}
                            editIcon={<RiEdit2Line className={Styles["editable-control-icon"]} />}
                            placeholder="SLA Policy description"
                            value={props.description}
                            isEditing={props.description === ""}
                            onChange={value => props.onChange({
                                description: value as string,
                                name: props.name,
                                values: severityValues,
                            })}
                        />

                    </Text>
                </div>
                <Button
                    variant="ghost-gray"
                    size="small"
                    type="button"
                    prefixIcon={<RiDeleteBinLine className={Styles["policy-header-delete"]} />}
                    onClick={props.onDelete}
                />
            </div>

            <div className={Styles["policy-table"]}>
                <div className={Styles["policy-headers"]}>
                    <Text textVariant="body-2-medium" colorVariant="body-secondary">
                        Priority
                    </Text>
                    {POLICIES.map(policy => (
                        <Text textVariant="body-2-medium" colorVariant="body-secondary" key={policy.metric}>
                            <div>
                                {policy.title}
                            </div>
                            <Popover
                                hasArrow
                                variant="tooltip"
                                size="sm"
                                text={(
                                    <RiQuestionLine className={Styles["policy-header-help"]} />
                                )}
                            >
                                {policy.description}
                            </Popover>
                        </Text>

                    ))}
                </div>

                {props.severityLabels.map((label, index) => (
                    <SLATierValueRow
                        key={label}
                        label={label}
                        value={severityValues[index]}
                        onChange={value => {
                            const copy = [...severityValues];
                            copy[index] = value;
                            props.onChange({
                                name: props.name,
                                description: props.description,
                                values: copy,
                            });
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

export default function SLASettings(props: SettingsProps): ReactElement {
    const [save] = useSaveSettingsMutation();
    useArticleContext("urn:fq:article:c13ef689cc9f415eba9c98c04d5f3312");

    const { properties, tracker } = useFormBuilder(
        builder => builder
            .array("severityLabels", () => props.settings?.sla?.severityLabels ?? [])
            .property("defaultSeverityIndex", () => props.settings?.sla?.defaultSeverityIndex ?? 0)
            .property("defaultSLAPolicyIndex", () => props.settings?.sla?.tiers?.findIndex(
                tier => tier.id === props.settings?.sla?.defaultSLAPolicyId,
            ) as number | undefined)
            .object("slaValues", () => props.settings?.sla?.tiers?.filterNull().map(
                tier => cloneWithoutTypename(tier) as SettingsSlaTierInput,
            ) ?? [], DEEP_COMPARER),
        [props.settings?.sla],
    );

    const { element: saveElement } = tracker.useDirtyTracker({
        finishedSavingMessage: "SLA settings saved.",
        onSave: (__, onDone) => {
            LoggingHandler.instance.wrap(async () => {
                await save({
                    refetchQueries: [
                        { query: CloseSettingsDocument },
                        { query: TagsSettingsDocument },
                        { query: GetSettingsDocument },
                        { query: GetSeverityLabelsDocument },
                    ],
                    variables: {
                        sla: {
                            severityLabels: properties.severityLabels.value,
                            defaultSeverityIndex: properties.defaultSeverityIndex.value,
                            defaultSLAPolicyIndex: properties.defaultSLAPolicyIndex.value,
                            tiers: properties.slaValues.value,
                        },
                    },
                });
                onDone();
            });
        },
    });

    const [isShowSeverities, setShowSeverities] = useState<boolean>();

    const createNewTier = () => properties.slaValues.setValue(current => ([...current, {
        name: "",
        description: "",
        values: [],
    }]));

    const defaultSeverityIndex = properties.defaultSeverityIndex.value;
    const defaultSLAPolicyIndex = properties.defaultSLAPolicyIndex.value;
    return (
        <>
            <SettingsContainer>
                <SettingsContainer.Section
                    title="Severity"
                    helpText="These general settings help you manage your sett"
                >
                    <SettingsContainer.Setting
                        title="Severities"
                        description="Severity levels allow you to prioritize tickets based on
                            their impact on your customers. You can create custom severity levels
                            to match your organization's needs."
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        {!isShowSeverities && properties.severityLabels.value.isEmpty ? (
                            <Dropdown className={Styles.presets}>
                                <Dropdown.Toggle
                                    variant="none"
                                    displayAs="select"
                                    postfixIcon={<RiArrowDownSLine />}
                                >
                                    Select Severities
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item
                                        onClick={() => {
                                            properties.severityLabels.setValue(["P0", "P1", "P2", "P3"]);
                                            properties.defaultSeverityIndex.setValue(2);
                                        }}
                                    >
                                        P0 &rarr; P4
                                    </Dropdown.Item>

                                    <Dropdown.Item
                                        onClick={() => {
                                            properties.severityLabels.setValue(["Blocker", "Critical", "Medium", "Low"]);
                                            properties.defaultSeverityIndex.setValue(2);
                                        }}
                                    >
                                        Blocker &rarr; Low
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                        onClick={
                                            () => setShowSeverities(true)
                                        }
                                    >
                                        Custom
                                    </Dropdown.Item>

                                </Dropdown.Menu>
                            </Dropdown>
                        ) : (
                            <TagList
                                value={properties.severityLabels.value}
                                onChange={properties.severityLabels.setValue}
                                canNotHaveDuplicates
                                placeholder="Add a severity"
                                className="flex-grow-1"
                            />
                        )}

                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="Default Severity"
                        description="What should be the default severity of a ticket if a severity is not yet specified?"
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isVisible={properties.severityLabels.value.isNotEmpty}
                        isLoading={props.isLoading}
                    >
                        <Select
                            options={properties.severityLabels.value.map((label, index) => ({
                                label,
                                value: index.toString(),
                                data: index,
                            }))}
                            size="small"
                            value={defaultSeverityIndex >= 0 ? ({
                                label: properties.severityLabels.value[defaultSeverityIndex],
                                value: defaultSeverityIndex.toString(),
                                data: defaultSeverityIndex,
                            }) : undefined}
                            onChange={current => properties.defaultSeverityIndex.setValue(current.data ?? 0)}
                            placeholder="What should be the default severity of a ticket if a severity is not yet specified?"
                        />
                    </SettingsContainer.Setting>

                </SettingsContainer.Section>
                <SettingsContainer.Section
                    title="SLA Policies"
                    helpText="The general settings will help you get started with sending emails and receiving emails."
                >

                    <SettingsContainer.Setting
                        title="Default Policy"
                        description="What should be the default policy when a policy is not explicitly specified for a channel?"
                        isVisible={properties.severityLabels.value.isNotEmpty && properties.slaValues.value.isNotEmpty}
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        isLoading={props.isLoading}
                    >
                        <Select
                            options={properties.slaValues.value.map((tier, index) => ({
                                label: tier.name ?? "",
                                value: index.toString(),
                            }))}
                            size="small"
                            value={defaultSLAPolicyIndex != null ? ({
                                label: properties.slaValues.value[defaultSLAPolicyIndex]?.name ?? "Untitled",
                                value: defaultSLAPolicyIndex.toString(),
                            }) : undefined}
                            isClearable
                            onChange={current => properties.defaultSLAPolicyIndex.setValue(
                                current != null ? parseInt(current.value, 10) : undefined,
                            )}
                            placeholder="What is your default policy?"
                        />
                    </SettingsContainer.Setting>
                    <SettingsContainer.Setting
                        title="SLA"
                        description="Customer support Service Level Agreement (SLA) Policies are agreements between a
                            company and its customers that define the expected level of service quality and support
                            provided by the company to its customers. These policies aim to establish clear expectations
                            for customer support, including the level of response time, problem resolution, and
                            communication. * Work Hours - when calculating the SLA due date, should it
                            include Hours of operation? For example, if the SLA is 16 hours but your Hours of
                            operation are 9-5, the 16 hour SLA will be scheduled for 2 days from now instead of 1."
                        descriptionElement={(
                            <>
                                <p className="mb-2">
                                    Customer support Service Level Agreement (SLA) Policies are agreements between a
                                    company and its customers that define the expected level of service quality and support
                                    provided by the company to its customers. These policies aim to establish clear expectations
                                    for customer support, including the level of response time, problem resolution, and
                                    communication.
                                </p>
                                <p>
                                    {" "}
                                    * Work Hours - when calculating the SLA due date, should it include Hours
                                    of operation? For example, if the SLA is 16 hours but your Hours of operation are
                                    9-5, the 16 hour SLA will be scheduled for 2 days from now instead of 1.
                                </p>
                            </>
                        )}
                        permissions={SettingsContainer.Setting.Permissions.Administrator}
                        className={Styles.policies}
                        isLoading={props.isLoading}
                    >
                        {properties.severityLabels.value.isEmpty ? (
                            <Alert variant="primary">
                                Please set up your severity levels
                                in the section above before creating an SLA policy.
                            </Alert>
                        ) : (
                            <>

                                {properties.slaValues.value.map((value, index) => (
                                    <SLATierRow
                                        key={value.id}
                                        name={value.name ?? ""}
                                        description={value.description ?? ""}
                                        severityLabels={properties.severityLabels.value}
                                        severityValues={value.values ?? []}
                                        onChange={tierValue => properties.slaValues.setValue(list => {
                                            const copy = [...list];
                                            const current = copy[index];
                                            copy[index] = {
                                                ...current,
                                                name: tierValue.name,
                                                values: tierValue.values,
                                                description: tierValue.description,
                                            };
                                            return copy;
                                        })}
                                        onDelete={() => {
                                            properties.slaValues.setValue(list => {
                                                const copy = [...list];
                                                copy.splice(index, 1);
                                                return copy;
                                            });
                                        }}
                                    />
                                ))}
                                <div className={Styles["create-tiers"]}>
                                    {properties.slaValues.value.isEmpty && (
                                        <PresetTiersComponent
                                            severityLabels={properties.severityLabels.value}
                                            setDefaultSLAPolicyIndex={properties.defaultSLAPolicyIndex.setValue}
                                            setSlaValues={properties.slaValues.setValue}
                                        />

                                    )}
                                    <Button
                                        type="button"
                                        prefixIcon={<RiAddLine />}
                                        variant="outline-gray"
                                        onClick={createNewTier}
                                        size="small"
                                    >
                                        Add SLA Policy
                                    </Button>

                                </div>
                            </>
                        )}

                    </SettingsContainer.Setting>
                </SettingsContainer.Section>

            </SettingsContainer>
            {saveElement}
        </>

    );
}
